/**
 * Login Page
 * Prozpector CRM - Stripe-inspired design with rose red accents
 * Supports MFA (TOTP) authentication flow
 */

import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { MFAVerification } from '@/components/auth/MFAVerification';
import { Eye, EyeOff, LogIn, AlertCircle, Loader2, Shield } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const { signIn, isAuthenticated, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // MFA state
  const [showMFA, setShowMFA] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  // Redirect if already authenticated (but not if we're in the middle of sign-in or showing MFA)
  useEffect(() => {
    if (isAuthenticated && !isLoading && !isSigningIn && !showMFA) {
      // Get the return URL from sessionStorage, or default to dashboard
      const returnTo = sessionStorage.getItem('returnTo') || '/dashboard';
      sessionStorage.removeItem('returnTo'); // Clean up
      setLocation(returnTo);
    }
  }, [isAuthenticated, isLoading, isSigningIn, showMFA, setLocation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSigningIn(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        // Check if this is an MFA required error
        if (error.message?.includes('mfa') || error.message?.includes('MFA')) {
          // Check for MFA factors
          await checkMFAFactors();
        } else {
          setError(error.message || 'Invalid email or password');
        }
      } else {
        // Check if user has MFA enabled - always require verification on explicit login
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const verifiedFactor = factors?.totp?.find(f => f.status === 'verified');

        if (verifiedFactor) {
          // User has MFA enabled - always require verification on login
          setMfaFactorId(verifiedFactor.id);
          setShowMFA(true);
        } else {
          // No MFA enabled, proceed to dashboard
          const returnTo = sessionStorage.getItem('returnTo') || '/dashboard';
          sessionStorage.removeItem('returnTo');
          setLocation(returnTo);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  // Check for MFA factors and show MFA verification if needed
  const checkMFAFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();

      if (error) {
        console.error('Error listing MFA factors:', error);
        setError('Failed to check MFA status');
        return;
      }

      // Find verified TOTP factor
      const totpFactor = data.totp.find(f => f.status === 'verified');

      if (totpFactor) {
        setMfaFactorId(totpFactor.id);
        setShowMFA(true);
      } else {
        // No MFA factor, proceed normally
        const returnTo = sessionStorage.getItem('returnTo') || '/dashboard';
        sessionStorage.removeItem('returnTo');
        setLocation(returnTo);
      }
    } catch (err) {
      console.error('MFA check error:', err);
      setError('Failed to verify MFA status');
    }
  };

  // Handle successful MFA verification
  const handleMFASuccess = () => {
    const returnTo = sessionStorage.getItem('returnTo') || '/dashboard';
    sessionStorage.removeItem('returnTo');
    setLocation(returnTo);
  };

  // Handle MFA cancellation
  const handleMFACancel = async () => {
    await supabase.auth.signOut();
    setShowMFA(false);
    setMfaFactorId(null);
    setEmail('');
    setPassword('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show MFA verification screen if MFA is required
  if (showMFA && mfaFactorId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <img
                src="/logo-square.svg"
                alt="Prozpector"
                className="w-16 h-16 rounded-xl shadow-lg"
              />
            </div>
            <img
              src="/logo-wordmark.svg"
              alt="Prozpector"
              className="h-10 mx-auto mb-2"
            />
          </div>

          {/* MFA Verification Component */}
          <MFAVerification
            factorId={mfaFactorId}
            onSuccess={handleMFASuccess}
            onCancel={handleMFACancel}
          />

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-gray-500">
            <p>Secure authentication powered by Supabase</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <SEO
        title="Log In - Prozpector"
        description="Log in to your Prozpector account and manage your cold prospecting campaigns."
        url="https://prozpector.com/login"
      />
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src="/logo-square.svg"
              alt="Prozpector"
              className="w-16 h-16 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
            />
          </div>
          <img
            src="/logo-wordmark.svg"
            alt="Prozpector"
            className="h-10 mx-auto mb-2"
          />
          <p className="text-gray-600">
            Professional CRM for Modern Teams
          </p>
        </div>

        {/* Login Card */}
        <Card className="shadow-xl border-gray-200 bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-semibold text-gray-900">Sign in</CardTitle>
            <CardDescription className="text-gray-600">
              Enter your email and password to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-900">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  required
                  disabled={isSigningIn}
                  className="w-full bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-primary-600 focus:ring-primary-600/20"
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-900">
                    Password
                  </Label>
                  <Link href="/forgot-password" className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    required
                    disabled={isSigningIn}
                    className="w-full pr-10 bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-primary-600 focus:ring-primary-600/20"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSigningIn}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <Alert variant="destructive" className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isSigningIn || !email || !password}
              >
                {isSigningIn ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Sign in
                  </>
                )}
              </Button>
            </form>

            {/* Book a Call Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Interested in our service?{' '}
                <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 font-medium">
                  Book a call
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Prozpector CRM</p>
          <p className="mt-1">Secure authentication powered by Supabase</p>
        </div>
      </div>
    </div>
  );
}
